import { useMutation } from '@apollo/client';
import { Alert, Button, Divider, Form, Input, message, Modal } from 'antd';
import clsx from 'clsx';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import Gleap from 'gleap';
import { filter, get, includes, map } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import countryCodeData from '../../assets/countryCode.json';
import { LoginLogo } from '../../assets/svg/login';
import {
  COUNTRY_CODES,
  COUNTRY_SORT_NAME,
  EQC_FILTER,
  EQC_STAGE_STATUS_KEYS,
  GA_EVENT,
  GA_LABEL,
  INS_STATUS_KEYS,
  ISSUE_FILTER,
  LOGIN_TYPE,
  ROUTES,
  SELECTED_TENANT_ID,
  TENANT_ID,
  TIMEOUT_FOR_OTP_IN_SECONDS,
  TIMEOUT_FOR_OTP_IN_SECONDS_FOR_WHATSAPP,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import useRouter from '../../common/useRouter';
import CommonSelect from '../../components/CommonSelect';
import auth from '../../firebase';
import {
  GENERATE_USER_OTP,
  LOGIN,
  LOGIN_USER_WITH_FIREBASE,
  LOGIN_USER_WITH_OTP,
  LOGIN_USER_WITH_WHATSAPP,
} from './graphql/Mutations';

const Verify = () => {
  const [isResent, setIsResent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showTenantSelectionModal, setShowTenantSelectionModal] =
    useState(false);
  const [tokenForOtherService, setTokenForOtherService] = useState();
  const { navigate, location } = useRouter();
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState();
  const [resendLoading, setResendLoading] = useState(false);
  const [getOtpClicked, setGetOtpClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const { initializeAuth, dispatch } = useContext(AppContext);
  const [loginType, setLoginType] = useState(location?.state?.loginType);
  const [form] = Form.useForm();
  const phone = location?.state?.phone;
  const countryCode = location?.state?.countryCode;
  const selectedCountryShortForm = location?.state?.countryShortForm;
  const pathname = location?.state?.pathname;
  const userEmail = location?.state?.email;
  const passwordInputRef = useRef();
  if ((!phone || !countryCode) && !userEmail) {
    navigate(ROUTES.LOGIN);
  }

  const successCallback = (accessToken, refreshToken) => {
    initializeAuth(accessToken, refreshToken);
    Gleap.initialize(process.env.REACT_APP_GLEAP_KEY);
    if (loginType === LOGIN_TYPE.OTP) {
      // eslint-disable-next-line no-undef
      localStorage.setItem(
        ISSUE_FILTER,
        JSON.stringify([INS_STATUS_KEYS?.RAISED, INS_STATUS_KEYS?.REJECTED]),
      );
      // eslint-disable-next-line no-undef
      localStorage.setItem(EQC_FILTER, EQC_STAGE_STATUS_KEYS?.APPROVAL_PENDING);
    }
    if (pathname && ![ROUTES.LOGOUT, ROUTES.VERIFY]?.includes(pathname)) {
      navigate(pathname);
      dispatch({
        type: 'ONE_TIME_LOGIN',
        data: true,
      });
    } else {
      navigate(ROUTES.MAIN);
      dispatch({
        type: 'ONE_TIME_LOGIN',
        data: true,
      });
    }
  };

  const [loginMutate, { loading: loginLoading }] = useMutation(LOGIN, {
    onError({ graphQLErrors }) {
      // eslint-disable-next-line no-undef
      localStorage.removeItem(TENANT_ID);
      setErrorMessage(graphQLErrors?.[0]?.message);
    },
  });

  const [loginUserWithOtp, { loading }] = useMutation(LOGIN_USER_WITH_OTP, {
    onError({ graphQLErrors }) {
      setErrorMessage(graphQLErrors?.[0]?.message);
    },
    onCompleted(res) {
      const tenantData = get(res, 'loginUserWithOTP.tenantUserData');
      if (res?.loginUserWithOTP?.tenantId) {
        // eslint-disable-next-line no-undef
        localStorage.setItem(TENANT_ID, res?.loginUserWithOTP?.tenantId);
      }
      if (tenantData) {
        if (tenantData?.length > 1) {
          setTenants(tenantData);
          setShowTenantSelectionModal(true);
        }
      } else {
        successCallback(
          res?.loginUserWithOTP?.token,
          res?.loginUserWithOTP?.refreshToken,
        );
      }
      Event(GA_EVENT.REQUESTED_OTP, {
        label: GA_LABEL.REQUESTED_OTP,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        phone,
        countryCode,
        email: userEmail,
      });
    },
  });

  const [loginUserWithFirebase] = useMutation(LOGIN_USER_WITH_FIREBASE, {
    onError({ graphQLErrors }) {
      setResendLoading(false);
      setErrorMessage(graphQLErrors?.[0]?.message);
    },
    onCompleted(res) {
      const tenantData = get(res, 'loginUserWithFirebase.tenantUserData');
      if (tenantData) {
        if (tenantData?.length > 1) {
          setTenants(tenantData);
          setShowTenantSelectionModal(true);
        }
      } else {
        successCallback(res?.loginUserWithFirebase?.token);
      }
      setIsLoading(false);
      Event(GA_EVENT.REQUESTED_OTP_WITH_FIREBASE, {
        label: GA_LABEL.REQUESTED_OTP_WITH_FIREBASE,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        phone,
        countryCode,
        email: userEmail,
      });
    },
  });

  const [loginUserWithWhatsApp] = useMutation(LOGIN_USER_WITH_WHATSAPP, {
    onError({ graphQLErrors }) {
      setIsLoading(false);
      setErrorMessage(graphQLErrors?.[0]?.message);
    },
    onCompleted(res) {
      const tenantData = get(res, 'loginUserWithWhatsApp.tenantUserData');
      if (tenantData) {
        if (tenantData?.length > 1) {
          setTenants(tenantData);
          setShowTenantSelectionModal(true);
        }
      } else {
        successCallback(res?.loginUserWithWhatsApp?.token);
      }
      setIsLoading(false);
      Event(GA_EVENT.REQUESTED_OTP_WITH_WHATSAPP, {
        label: GA_LABEL.REQUESTED_OTP_WITH_WHATSAPP,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        phone,
        countryCode,
        email: userEmail,
      });
    },
  });

  const [generateUserOtp] = useMutation(GENERATE_USER_OTP, {
    onError({ graphQLErrors }) {
      setErrorMessage(graphQLErrors?.[0]?.message);
    },
    onCompleted() {
      setResendLoading(false);
      Event(GA_EVENT.RESEND_OTP, {
        label: GA_LABEL.RESEND_OTP,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        phone,
        countryCode,
        email: userEmail,
      });
    },
  });

  useEffect(() => {
    passwordInputRef?.current?.focus();
  }, []);

  useEffect(() => {
    if (
      countryCode &&
      !includes([COUNTRY_CODES.IN, COUNTRY_CODES.AE], countryCode)
    ) {
      setTimeLeft(TIMEOUT_FOR_OTP_IN_SECONDS_FOR_WHATSAPP);
      setIsResent(true);
    }
  }, [countryCode]);

  useEffect(() => {
    let intervalRef;
    if (isResent) {
      intervalRef = setInterval(() => {
        setTimeLeft((currentTime) => {
          if (currentTime - 1 === 0) {
            setIsResent(false);
          }
          return currentTime - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef);
    }
    return () => {
      clearInterval(intervalRef);
    };
  }, [isResent]);

  useEffect(() => {
    try {
      // eslint-disable-next-line no-undef
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        'recaptcha-container',
        { size: 'invisible' },
      );
    } catch (error) {
      return error;
    }
  }, []);

  const loginWithSelectedTenant = async (tenantData, tenantId) => {
    const value = form.getFieldsValue(true);
    const { password, otp } = value;
    // eslint-disable-next-line no-undef
    localStorage.setItem(TENANT_ID, tenantId);
    if (!loginLoading) {
      if (password) {
        const countryShortForm = filter(
          countryCodeData,
          (record) => record?.dialCode === countryCode,
        )?.[0]?.countryCode?.toUpperCase();
        const selectedTenantResponse = await loginMutate({
          variables: {
            data: {
              email: userEmail,
              phoneNo: phone,
              country: countryShortForm,
              password,
              webAccess: true,
            },
          },
        });
        const newAccessToken = get(selectedTenantResponse, 'data.login.token');
        const newRefreshToken = get(
          selectedTenantResponse,
          'data.login.refreshToken',
        );
        if (newAccessToken && newRefreshToken) {
          successCallback(newAccessToken, newRefreshToken);
        } else {
          setShowTenantSelectionModal(false);
        }
      } else if (COUNTRY_CODES.IN === countryCode) {
        await loginUserWithOtp({
          variables: {
            data: {
              phoneNo: phone,
              country: COUNTRY_SORT_NAME.IN,
              otp: parseInt(otp, 10),
            },
          },
        });
      } else {
        setIsLoading(true);
        const countryShortForm = filter(
          countryCodeData,
          (record) => record?.dialCode === countryCode,
        )?.[0]?.countryCode?.toUpperCase();
        if (countryCode === COUNTRY_CODES.AE) {
          await loginUserWithFirebase({
            variables: {
              data: {
                firebaseToken: tokenForOtherService,
                country: countryShortForm,
                phoneNo: phone,
              },
            },
          });
        } else {
          await loginUserWithWhatsApp({
            variables: {
              data: {
                accessToken: tokenForOtherService,
                country: countryShortForm,
                phoneNo: phone,
              },
            },
          });
        }
      }
      setTokenForOtherService();
    }
  };

  const onFinish = async ({ otp, password }) => {
    if (
      loginType === LOGIN_TYPE.OTP ||
      (loginType === LOGIN_TYPE.PASSWORD && phone && getOtpClicked)
    ) {
      if (COUNTRY_CODES.IN === countryCode) {
        loginUserWithOtp({
          variables: {
            data: {
              phoneNo: phone,
              country: COUNTRY_SORT_NAME.IN,
              otp: parseInt(otp, 10),
            },
          },
        });
      } else {
        setIsLoading(true);
        const countryShortForm = filter(
          countryCodeData,
          (record) => record?.dialCode === countryCode,
        )?.[0]?.countryCode?.toUpperCase();
        if (countryCode === COUNTRY_CODES.AE) {
          // eslint-disable-next-line no-undef
          window?.confirmationResult
            ?.confirm(otp)
            ?.then(async () => {
              auth?.currentUser?.getIdToken()?.then(async (res) => {
                setTokenForOtherService(res);
                await loginUserWithFirebase({
                  variables: {
                    data: {
                      firebaseToken: res,
                      country: countryShortForm,
                      phoneNo: phone,
                    },
                  },
                });
              });
            })
            ?.catch((error) => {
              setIsLoading(false);
              message.destroy();
              message.error(
                error?.code === 'auth/code-expired'
                  ? 'OTP Expired'
                  : 'Invalid OTP',
              );
            });
        } else {
          // eslint-disable-next-line no-undef
          window?.verifyOtp(
            parseInt(otp, 10),
            (data) => {
              setTokenForOtherService(data?.message);
              loginUserWithWhatsApp({
                variables: {
                  data: {
                    accessToken: data?.message,
                    country: countryShortForm,
                    phoneNo: phone,
                  },
                },
              });
            },
            (error) => {
              setIsLoading(false);
              message.destroy();
              message.error(error?.message);
            },
          );
        }
      }
    }
    if (userEmail || loginType === LOGIN_TYPE.PASSWORD) {
      try {
        const countryShortForm = filter(
          countryCodeData,
          (record) => record?.dialCode === countryCode,
        )?.[0]?.countryCode?.toUpperCase();
        const response = await loginMutate({
          variables: {
            data: {
              email: userEmail,
              phoneNo: phone,
              password,
              country: countryShortForm,
              webAccess: true,
            },
          },
        });

        const errorResponse = response?.errors?.message;
        const accessToken = get(response, 'data.login.token');
        const refreshToken = get(response, 'data.login.refreshToken');
        const tenantData = get(response, 'data.login.tenantUserData');
        const tenantId = get(response, 'data.login.tenantId');

        if (tenantData) {
          if (tenantData?.length > 1) {
            setTenants(tenantData);
            setShowTenantSelectionModal(true);
          } else {
            loginWithSelectedTenant(tenantData, tenantData[0]?.tenantId);
          }
        } else if (!errorResponse) {
          // eslint-disable-next-line no-undef
          localStorage.setItem(TENANT_ID, tenantId);
          successCallback(accessToken, refreshToken);
        } else {
          form.resetFields(['password']);
        }
      } catch (error) {
        return error;
      }
    }
  };

  const handleResend = () => {
    setResendLoading(true);
    setIsResent(true);
    if (COUNTRY_CODES.IN === countryCode) {
      setTimeLeft(TIMEOUT_FOR_OTP_IN_SECONDS);
      generateUserOtp({
        variables: {
          data: {
            phoneNo: `${COUNTRY_CODES.IN}${phone}`,
            country: COUNTRY_CODES.IN,
          },
        },
      });
    } else if (COUNTRY_CODES.AE === countryCode) {
      setTimeLeft(TIMEOUT_FOR_OTP_IN_SECONDS);
      signInWithPhoneNumber(
        auth,
        `${countryCode}${phone}`,
        // eslint-disable-next-line no-undef
        window?.recaptchaVerifier,
      )
        ?.then((confirmationResult) => {
          Event(GA_EVENT.RESEND_OTP_WITH_FIREBASE, {
            label: GA_LABEL.RESEND_OTP_WITH_FIREBASE,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            phone,
            countryCode,
            email: userEmail,
          });
          setResendLoading(false);
          setIsResent(true);
          // eslint-disable-next-line no-undef
          window.confirmationResult = confirmationResult;
          message.success('Otp sent successfully!');
        })
        ?.catch(() => {
          setResendLoading(false);
          message.destroy();
          message.error('Something went wrong!');
        });
    } else {
      setTimeLeft(TIMEOUT_FOR_OTP_IN_SECONDS_FOR_WHATSAPP);
      // eslint-disable-next-line no-undef
      window?.retryOtp(
        '12',
        () => {
          Event(GA_EVENT.RESEND_OTP_WITH_WHATSAPP, {
            label: GA_LABEL.RESEND_OTP_WITH_WHATSAPP,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            phone,
            countryCode,
            email: userEmail,
          });
          setResendLoading(false);
          setIsResent(true);
        },
        (error) => {
          setResendLoading(false);
          message.destroy();
          message.error(error?.message);
        },
      );
    }
  };

  const handleGetOtpClick = () => {
    setGetOtpClicked(true);
    setLoginType(LOGIN_TYPE.OTP);
    const countryShortForm = filter(
      countryCodeData,
      (record) => record?.dialCode === countryCode,
    )?.[0]?.countryCode?.toUpperCase();
    if (countryCode === COUNTRY_CODES.IN) {
      generateUserOtp({
        variables: {
          data: {
            phoneNo: `${countryCode}${phone}`,
            country: countryShortForm,
          },
        },
      });
    } else if (countryCode === COUNTRY_CODES.AE) {
      const phoneNo = `${countryCode}${phone}`;
      // eslint-disable-next-line no-undef
      signInWithPhoneNumber(auth, phoneNo, window.recaptchaVerifier)
        ?.then((result) => {
          // eslint-disable-next-line no-undef
          window.confirmationResult = result;
          message.success('Otp sent successfully!');
          Event(GA_EVENT.REQUESTED_OTP_WITH_FIREBASE, {
            label: GA_LABEL.REQUESTED_OTP_WITH_FIREBASE,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            phone: phoneNo,
            countryCode,
          });
        })
        ?.catch(() => {
          message.destroy();
          message.error('Something went wrong!');
        });
    } else {
      // eslint-disable-next-line no-undef
      window?.sendOtp(`${countryCode?.replace('+', '')}${phone}`, () => {
        message.success('Otp sent successfully!');
      });
      Event(GA_LABEL.REQUESTED_OTP_WITH_WHATSAPP, {
        label: GA_LABEL.REQUESTED_OTP_WITH_WHATSAPP,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        phone,
        countryCode,
      });
    }
  };

  const renderInputField = () => {
    return (
      <>
        <Form.Item
          name={
            userEmail || loginType === LOGIN_TYPE.PASSWORD ? 'password' : 'otp'
          }
          label={
            <div className="mt-5 d-flex justify-between width-percent-100">
              <div>
                {userEmail || loginType === LOGIN_TYPE.PASSWORD
                  ? 'Password'
                  : 'Enter OTP'}
              </div>
              {(userEmail || loginType === LOGIN_TYPE.PASSWORD) && (
                <div>
                  <Link state={{ email: userEmail }} to={ROUTES.FORGOT}>
                    Forgot password ?
                  </Link>
                </div>
              )}
            </div>
          }
        >
          {userEmail || loginType === LOGIN_TYPE.PASSWORD ? (
            <Input.Password autoFocus ref={passwordInputRef} />
          ) : (
            <Input autoFocus maxLength={6} ref={passwordInputRef} />
          )}
        </Form.Item>
      </>
    );
  };

  return (
    <div className="gx-login-container">
      <div id="recaptcha-container" className="visibility-hidden" />

      <div
        className="login-logo pointer"
        onClick={() => {
          navigate(ROUTES.LOGIN);
        }}
      >
        <LoginLogo width="200" height="60" />
      </div>
      {errorMessage && (
        <Alert
          message={<h3>There was a problem</h3>}
          description={errorMessage}
          type="error"
          showIcon
        />
      )}
      <div className="gx-login-content">
        <div className="card-body">
          <h1>
            {loginType === LOGIN_TYPE.OTP || getOtpClicked
              ? 'Authentication required'
              : `Sign in`}
          </h1>
          <p className="d-flex align-center mb-0">
            {userEmail || `${selectedCountryShortForm} ${countryCode}${phone}`}
            <Link
              to={ROUTES.LOGIN}
              state={{ email: userEmail, countryCode, phone }}
              className="ml-5"
            >
              Change
            </Link>
          </p>
          {(loginType === LOGIN_TYPE.OTP || getOtpClicked) && (
            <p>
              We’ve sent a One Time Password (OTP) to the mobile number above.
              Please enter it to complete verification
            </p>
          )}
          <Form
            layout="vertical"
            name="Login"
            className="gx-login-form gx-form-row0"
            form={form}
            onFinish={onFinish}
          >
            {renderInputField()}
            <div
              className={` align-center mt-15 ${loginType === LOGIN_TYPE.OTP ? 'justify-start' : 'justify-end'}`}
            >
              <Button
                type="primary"
                htmlType="submit"
                shape="round"
                className="mt-10"
                loading={loading || isLoading}
              >
                Sign in
              </Button>
              <div>
                {loginType === LOGIN_TYPE.PASSWORD && phone && (
                  <>
                    <Divider>or</Divider>
                    <Button
                      className="secondary-button"
                      shape="round"
                      onClick={handleGetOtpClick}
                      loading={loading || isLoading}
                    >
                      Get an OTP on your phone
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Form>
          {loginType === LOGIN_TYPE.OTP ||
          (loginType === LOGIN_TYPE.PASSWORD && phone && getOtpClicked) ? (
            <>
              <div className={clsx('d-flex mt-15 justify-center')}>
                {isResent ? (
                  <div className="text-secondary mr-5">
                    Send again in {timeLeft} seconds
                  </div>
                ) : (
                  <div className={clsx('text-secondary d-flex mr-5')}>
                    <div
                      className="text-primary pointer ml-5"
                      role="button"
                      onClick={resendLoading ? undefined : handleResend}
                      tabIndex={0}
                    >
                      {resendLoading ? 'Sending...' : 'Resend OTP'}
                    </div>
                  </div>
                )}
              </div>
              <Divider>or</Divider>
              <Button
                shape="round"
                className="mt-10 secondary-button"
                loading={loading || isLoading}
                onClick={() => {
                  setLoginType(LOGIN_TYPE.PASSWORD);
                  setGetOtpClicked(false);
                }}
              >
                Sign in with your password
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {showTenantSelectionModal && (
        <div id="tenant-list">
          <Modal
            open={showTenantSelectionModal}
            onOk={() => loginWithSelectedTenant(tenants, selectedTenantId)}
            onCancel={() => setShowTenantSelectionModal(false)}
            okButtonProps={{
              loading: loginLoading,
              shape: 'round',
              disabled: !selectedTenantId,
              className: !selectedTenantId && 'no-hover',
            }}
            okText="Proceed"
            cancelButtonProps={{ shape: 'round' }}
            centered
            closable={false}
            // eslint-disable-next-line no-undef
            getContainer={() => document.getElementById('tenant-list')}
          >
            <div className="mb-10">
              <span className="required-mark">*</span>Select organization to
              Proceed
            </div>
            <CommonSelect
              placeholder="Select organization to login with"
              onChange={(id) => {
                setSelectedTenantId(id);
                // eslint-disable-next-line no-undef
                localStorage.setItem(SELECTED_TENANT_ID, id);
              }}
              className="width-percent-100"
              defaultValue={() => {
                // eslint-disable-next-line no-undef
                const tenantId = localStorage.getItem('SELECTED_TENANT_ID');
                const selectedTenant =
                  filter(
                    tenants,
                    (value) => value?.tenantId === Number(tenantId),
                  )?.[0]?.tenantId || null;
                setSelectedTenantId(selectedTenant);
                return selectedTenant;
              }}
              showSearch
              optionFilterProp="label"
              options={[
                ...map(tenants, (tenant) => {
                  return {
                    key: tenant?.tenantId,
                    value: tenant?.tenantId,
                    label: tenant?.tenant?.organizationName,
                  };
                }),
              ]}
            />
          </Modal>
        </div>
      )}
    </div>
  );
};

export default Verify;
